import React from 'react';
import {
  AbsoluteFill,
  Audio,
  Video,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';
import { TransitionSeries, linearTiming, TransitionPresentation } from '@remotion/transitions';
import { fade } from '@remotion/transitions/fade';
import { slide } from '@remotion/transitions/slide';
import { wipe } from '@remotion/transitions/wipe';
import { clockWipe } from '@remotion/transitions/clock-wipe';
import { SceneBackground, GradientBackground } from '../shared/components/SceneBackground';
import { SyncedCaption } from '../shared/components/SyncedCaption';
import { SceneEffects } from '../shared/components/SceneEffects';
import {
  SCENES,
  LOGO_SCENE,
  msToFrames,
  type Scene,
} from './types';

// Get transition for each scene change based on index
// Each transition is chosen to match the emotional shift
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getSceneTransition = (index: number, width: number, height: number): TransitionPresentation<any> => {
  switch (index) {
    case 0: // Scene 1→2 (Hook→Contrast): Competitor pushes in from left
      return slide({ direction: 'from-left' });
    case 1: // Scene 2→3 (Contrast→Setup): Calm fade
      return fade();
    case 2: // Scene 3→4 (Setup→Pain): Building tension
      return fade();
    case 3: // Scene 4→5 (Pain→Why?): Dramatic reveal
      return wipe({ direction: 'from-left' });
    case 4: // Scene 5→6 (Why?→Solution): Answer arrives from right
      return slide({ direction: 'from-right' });
    case 5: // Scene 6→7 (Solution→CTA): Attention-grabbing clock wipe
      return clockWipe({ width, height });
    default:
      return fade();
  }
};

// Toggle between AI-generated backgrounds and gradient fallback
// Set to true after running: npx tsx scripts/generate-gdm-v3-images.ts
const USE_AI_BACKGROUNDS = true;

/**
 * Scene component that renders ONLY the background (no captions)
 * Captions are rendered by GlobalCaptions outside TransitionSeries for accurate timing
 */
interface SceneComponentProps {
  scene: Scene;
}

const SceneComponent: React.FC<SceneComponentProps> = ({ scene }) => {
  const { fps } = useVideoConfig();

  const sceneDurationFrames = msToFrames(scene.endMs - scene.startMs, fps);

  return (
    <AbsoluteFill>
      {/* Background only - captions handled globally */}
      {USE_AI_BACKGROUNDS ? (
        <SceneBackground
          imagePath={scene.backgroundImage}
          kenBurns={scene.kenBurns}
          durationInFrames={sceneDurationFrames}
          overlayOpacity={0.45}
        />
      ) : (
        <GradientBackground
          primaryColor={scene.primaryColor}
        />
      )}
    </AbsoluteFill>
  );
};

/**
 * Global captions component - renders OUTSIDE TransitionSeries
 * Uses the composition's global frame for accurate audio sync
 */
const GlobalCaptions: React.FC = () => {
  const frame = useCurrentFrame(); // This is the GLOBAL frame!
  const { fps } = useVideoConfig();

  // Convert global frame to absolute milliseconds
  const currentMs = (frame / fps) * 1000;

  // Find current scene based on absolute timing
  const currentScene = SCENES.find(
    (scene) => currentMs >= scene.startMs && currentMs < scene.endMs
  );

  // Don't render captions during logo scene or if no scene found
  if (!currentScene) return null;

  return (
    <SyncedCaption
      words={currentScene.words}
      currentMs={currentMs}
      primaryColor={currentScene.primaryColor}
      accentColor={currentScene.accentColor}
      fontSize={72}
      highlightCurrentWord={true}
      layout="center"
      overrideFirstWordColor={false}
    />
  );
};

/**
 * Enhanced animated logo reveal with glow and particles
 * (V3-specific: dark background with gold glow)
 */
const LogoScene: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const fadeIn = interpolate(
    frame,
    [0, fps * 0.5],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );

  const scale = spring({
    frame,
    fps,
    config: { damping: 8, stiffness: 100, mass: 1 },
  });

  const scaleValue = interpolate(scale, [0, 1], [0.6, 1]);

  const glowPulse = interpolate(
    Math.sin(frame * 0.15),
    [-1, 1],
    [0.3, 0.6],
    { extrapolateRight: 'clamp' }
  );

  const particleOffset = frame * 0.5;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#0a0a12',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: `radial-gradient(ellipse at center, rgba(252, 211, 77, ${glowPulse * 0.15}) 0%, #1a1a3e 30%, #0a0a12 70%)`,
        }}
      />
      <div
        style={{
          position: 'absolute',
          width: '200%',
          height: '200%',
          background: `
            radial-gradient(circle at ${30 + Math.sin(particleOffset * 0.02) * 10}% ${40 + Math.cos(particleOffset * 0.03) * 10}%, rgba(252, 211, 77, 0.1) 0%, transparent 20%),
            radial-gradient(circle at ${70 + Math.cos(particleOffset * 0.025) * 10}% ${60 + Math.sin(particleOffset * 0.02) * 10}%, rgba(252, 211, 77, 0.08) 0%, transparent 15%),
            radial-gradient(circle at ${50 + Math.sin(particleOffset * 0.015) * 15}% ${30 + Math.cos(particleOffset * 0.02) * 10}%, rgba(255, 255, 255, 0.05) 0%, transparent 10%)
          `,
          opacity: fadeIn,
        }}
      />
      <div
        style={{
          opacity: fadeIn,
          transform: `scale(${scaleValue})`,
          filter: `drop-shadow(0 0 ${30 * glowPulse}px rgba(252, 211, 77, ${glowPulse}))`,
        }}
      >
        <Video
          src={staticFile('logos/garage-marketers.mp4')}
          style={{
            width: '70%',
            height: 'auto',
            objectFit: 'contain',
          }}
        />
      </div>
    </AbsoluteFill>
  );
};

/**
 * Main GDM Quiet Phone V3 Composition
 */
export const GDMQuietPhoneV3: React.FC = () => {
  const { fps, width, height } = useVideoConfig();

  const TRANSITION_FRAMES = 10;

  const getSceneDuration = (scene: Scene, index: number) => {
    const baseDuration = msToFrames(scene.endMs - scene.startMs, fps);
    const isFirst = index === 0;
    const isLast = index === SCENES.length - 1;
    if (isFirst || isLast) {
      return baseDuration + Math.floor(TRANSITION_FRAMES / 2);
    }
    return baseDuration + TRANSITION_FRAMES;
  };

  return (
    <AbsoluteFill>
      <Audio
        src={staticFile('audio/gdm-quiet-phone-voiceover-v3.mp3')}
      />

      <TransitionSeries>
        {SCENES.map((scene, i) => (
          <React.Fragment key={scene.id}>
            <TransitionSeries.Sequence
              durationInFrames={getSceneDuration(scene, i)}
            >
              <SceneComponent scene={scene} />
            </TransitionSeries.Sequence>
            {i < SCENES.length - 1 && (
              <TransitionSeries.Transition
                presentation={getSceneTransition(i, width, height)}
                timing={linearTiming({ durationInFrames: TRANSITION_FRAMES })}
              />
            )}
          </React.Fragment>
        ))}

        <TransitionSeries.Transition
          presentation={fade()}
          timing={linearTiming({ durationInFrames: TRANSITION_FRAMES })}
        />

        <TransitionSeries.Sequence
          durationInFrames={msToFrames(LOGO_SCENE.endMs - LOGO_SCENE.startMs, fps) + Math.floor(TRANSITION_FRAMES / 2)}
        >
          <LogoScene />
        </TransitionSeries.Sequence>
      </TransitionSeries>

      <SceneEffects scenes={SCENES} />

      <GlobalCaptions />
    </AbsoluteFill>
  );
};

export default GDMQuietPhoneV3;

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
